/**
 * PKCE (RFC 7636) helpers.
 *
 * The broker's app clients are public - GenerateSecret is false - so the authorization code is the
 * only thing protecting the token exchange. PKCE binds that code to this browser session. Cognito
 * accepts code_challenge/code_verifier on a public client with no server-side configuration, so
 * nothing in the CDK stack or the CLI has to change for this.
 *
 * crypto.subtle requires a secure context, which https and http://localhost both satisfy.
 */

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** 32 random bytes as base64url - 43 characters, inside RFC 7636's 43-128 range. */
export function randomUrlSafe(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

export function createVerifier(): string {
  return randomUrlSafe(32);
}

/** The S256 challenge: base64url(SHA-256(ascii(verifier))). */
export async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}
